'use client';

import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Card';
import { Icon } from '@/components/ui/Icon';
import { Section, SectionHeading } from '@/components/ui/Section';
import { ROUTES } from '@/config/routes';
import { getProduct } from '@/data/products';
import { formatPrice } from '@/lib/format';

const APP_FEATURES = [
  {
    icon: 'touch' as const,
    title: 'Tap, type, done',
    description: 'Customers tap the screen and enter their phone number on an on-screen keypad. No app to download.',
  },
  {
    icon: 'megaphone' as const,
    title: 'Instant SMS with your link',
    description: 'They immediately receive a text with your signup link and the promotion you are currently running.',
  },
  {
    icon: 'remote' as const,
    title: 'Updated remotely',
    description: 'Swap the advertising video or promotion from the dashboard — no visit to the venue needed.',
  },
  {
    icon: 'shield' as const,
    title: 'Locked-down kiosk',
    description: 'The tablet runs in kiosk mode, so customers can only use the signup screen and nothing else.',
  },
];

/** Task 5: "we have a tablet version that's cheap and flexible — ask us about it". */
export function TabletAppShowcase({ tone = 'default' }: { tone?: 'default' | 'raised' }) {
  const mini = getProduct('launchpad-mini');
  const pro = getProduct('launchpad-pro');

  return (
    <Section id="tablet-app" tone={tone}>
      <SectionHeading
        eyebrow="Tablet app version"
        title="The cheap, flexible way to start"
        description="Not ready for a metal cabinet? The tablet LaunchPad runs exactly the same software on a commercial Android tablet — a fraction of the price, and it fits on any counter."
      />

      <div className="mt-14 grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="relative">
          <div className="from-brand/20 absolute -inset-5 rounded-[2.5rem] bg-gradient-to-br via-transparent to-cyan-400/15 blur-2xl" />
          <OptimizedImage
            src="/images/launchpad-tablet-in-store.png"
            alt="A LaunchPad tablet running the SPK2GO signup screen on a convenience store counter"
            width={540}
            height={930}
            className="border-line bg-ink/60 relative mx-auto h-auto w-full max-w-md rounded-[2rem] border object-cover p-3"
          />
        </div>

        <div className="flex flex-col gap-8">
          <div className="grid gap-5 sm:grid-cols-2">
            {[mini, pro].map((product) =>
              product ? (
                <div key={product.id} className="border-line bg-surface/60 flex flex-col gap-2 rounded-2xl border p-5">
                  <p className="text-content-subtle text-xs tracking-[0.2em] uppercase">{product.tagline}</p>
                  <h3 className="text-xl text-white">{product.name}</h3>
                  <p className="font-display text-3xl font-bold text-white">{formatPrice(product.priceUsd)}</p>
                  <p className="text-content-subtle text-xs">{product.priceNote}</p>
                </div>
              ) : null,
            )}
          </div>

          <ul className="flex flex-col gap-5">
            {APP_FEATURES.map((feature) => (
              <li key={feature.title} className="flex gap-4">
                <span className="border-brand/30 bg-brand/10 text-brand-bright inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border">
                  <Icon name={feature.icon} className="h-5 w-5" />
                </span>
                <div>
                  <h4 className="font-semibold text-white">{feature.title}</h4>
                  <p className="text-content-muted mt-1 text-sm leading-relaxed">{feature.description}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button to={`${ROUTES.buy}?product=launchpad-mini`}>Order a tablet LaunchPad</Button>
            <Button variant="secondary" to={ROUTES.contact}>
              Ask us about it
            </Button>
          </div>

          <Badge tone="success" className="self-start">
            <Icon name="check" className="h-3.5 w-3.5" /> App included free
          </Badge>
        </div>
      </div>
    </Section>
  );
}
